/*
 * The sound and the vibration the warehouse operator gets for every outcome:
 * a rising tone for what the server confirmed, two low ones for what it
 * refused, a double one for what needs a decision, a tick for a step accepted.
 *
 * Plain functions, no React: `features/operator/operatorListeners` calls them
 * when the band changes. Tones are synthesised, so there is no audio file to
 * load on a slow warehouse connection.
 */

export const AUDIO_STATE = {
  LOCKED: 'locked',
  READY: 'ready',
  UNAVAILABLE: 'unavailable',
};

const TONES = {
  success: [
    { frequency: 880, at: 0, length: 0.09 },
    { frequency: 1320, at: 0.1, length: 0.12 },
  ],
  error: [
    { frequency: 220, at: 0, length: 0.18, type: 'square' },
    { frequency: 220, at: 0.24, length: 0.18, type: 'square' },
  ],
  warning: [
    { frequency: 660, at: 0, length: 0.1 },
    { frequency: 660, at: 0.16, length: 0.1 },
  ],
  step: [{ frequency: 1200, at: 0, length: 0.035 }],
};

const VIBRATIONS = {
  success: 40,
  error: [120, 60, 120],
  warning: [60, 40, 60],
  step: 15,
};

const VOLUME = 0.15;

let context = null;

const audioContextClass = () =>
  (typeof window !== 'undefined' && (window.AudioContext || window.webkitAudioContext)) || null;

/**
 * Lets the page play sound. iOS only allows it from inside a user's tap, so
 * this must run synchronously within one; older iOS also needs something to
 * actually play, hence the silent sample.
 *
 * @returns {string} One of `AUDIO_STATE`.
 */
export const unlockAudio = () => {
  const AudioContextClass = audioContextClass();
  if (!AudioContextClass) return AUDIO_STATE.UNAVAILABLE;

  try {
    if (!context) context = new AudioContextClass();
    if (context.state === 'suspended') context.resume();

    const silence = context.createBufferSource();
    silence.buffer = context.createBuffer(1, 1, 22050);
    silence.connect(context.destination);
    silence.start(0);
    return AUDIO_STATE.READY;
  } catch {
    return AUDIO_STATE.UNAVAILABLE;
  }
};

/**
 * Plays a tone. Answers `false` when the browser took the audio back — iOS
 * does when the app goes to the background — so it can be unlocked again.
 *
 * @param {string} tone - success | error | warning | step
 * @returns {boolean}
 */
export const playSignal = (tone) => {
  if (!context || context.state !== 'running') return false;

  const start = context.currentTime;
  (TONES[tone] || []).forEach(({ frequency, at, length, type = 'sine' }) => {
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = type;
    oscillator.frequency.value = frequency;
    gain.gain.setValueAtTime(0.0001, start + at);
    gain.gain.exponentialRampToValueAtTime(VOLUME, start + at + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + at + length);
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start(start + at);
    oscillator.stop(start + at + length + 0.02);
  });
  return true;
};

/** Android vibrates; iOS browsers do not offer it at all. */
export const canVibrate = () => typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function';

/** @param {string} tone */
export const vibrateSignal = (tone) => {
  if (canVibrate() && VIBRATIONS[tone]) navigator.vibrate(VIBRATIONS[tone]);
};
